import React from 'react'
import withStyles from '@material-ui/core/styles/withStyles'
import dayjs from 'dayjs'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Input from '@material-ui/core/Input'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import FormHelperText from '@material-ui/core/FormHelperText'
import Icon from '@material-ui/core/Icon'
import InputMask from 'react-input-mask'
import { withAppFrame } from 'shared-ui/components/AppFrameContext'
import {
  getMobileDateMask,
  convertToStandardFormat,
  getLocaleFormat,
  isDateWithinConstraints,
} from './dateHelper'
import IntlUtil from '../../../utils/IntlUtil'
import CircularProgress from '@material-ui/core/CircularProgress'

class TextInputField extends React.Component {
  constructor(props) {
    super(props)
    const { value, data: { type, size } = {}, appFrame } = props
    const locale = TextInputField._getLocale(appFrame)
    this.state = {
      value: TextInputField._toInputValue(value, type, size, locale),
    }
  }

  static _getLocale(appFrame) {
    const lang = appFrame?.settings?.language
    return lang?.locale || lang?.id || IntlUtil.locale
  }

  static _toInputValue(value, type, size, locale) {
    if (type !== 'date' || !value) return value
    const parsed = dayjs(value)
    if (!parsed.isValid()) return value
    if (size === 'xs') return parsed.format(getLocaleFormat(locale))
    return parsed.format('YYYY-MM-DD')
  }

  componentDidUpdate(prevProps) {
    const { value, data: { type, size } = {}, appFrame } = this.props
    const locale = TextInputField._getLocale(appFrame)
    const prevSize = prevProps.data?.size

    if (type !== 'date') return

    if (prevProps.value !== value) {
      this.setState({ value: TextInputField._toInputValue(value, type, size, locale) })
    } else if (prevSize !== size) {
      const currentValue = this.state.value
      const asStandard =
        prevSize === 'xs' ? convertToStandardFormat(currentValue, locale) : currentValue
      this.setState({ value: TextInputField._toInputValue(asStandard, type, size, locale) })
    }
  }

  render() {
    const {
      classes,
      data,
      value,
      className,
      invalid,
      onBlur,
      onFocus,
      tabIndex,
      tipRender,
      fullWidth,
      disabled,
      theme,
      autoFocus,
      customStyle,
      pending,
      hasExternalValidation,
      endAdornment,
      appFrame,
    } = this.props

    const locale = TextInputField._getLocale(appFrame)
    let {
      id,
      readOnly,
      placeholder,
      required,
      pattern,
      multiline,
      type,
      rows,
      max,
      min,
      size,
      inputMode,
    } = data

    let errorText
    if (this.props.errorText) {
      errorText = this.props.errorText
    } else {
      errorText = data.errorText
    }

    const isPassword = data.type === 'password'
    const inputMask = this.getInputMask()

    let defaultType = type
    let defaultInputMode = inputMode || 'text'
    let inputPlaceholder

    if (isPassword && this.state.showPassword) defaultType = 'text'

    if (type === 'date' && size === 'xs') {
      defaultType = 'text'
      defaultInputMode = 'numeric'
      inputPlaceholder = getLocaleFormat(locale)
    }

    let defaultMax
    if (max === 'now' && type === 'date') {
      defaultMax = convertToStandardFormat(new Date())
    }

    const inputProps = {
      autoFocus,
      max: defaultMax || max,
      min: min,
      inputmode: defaultInputMode,
      tabIndex: tabIndex,
      pattern: pattern,
      placeholder: customStyle ? placeholder : inputPlaceholder,
      className:
        isPassword && !this.state.showPassword
          ? classes.passwordBullets
          : multiline
            ? classes.inputTextMulti
            : classes.inputText,
      'data-testid': `input-${id}`,
    }

    const form = (
      <FormControl fullWidth={fullWidth} className={className} error={!!invalid}>
        {!customStyle && (
          <InputLabel htmlFor={id} required={required} shrink={defaultType === 'date' || undefined}>
            {placeholder}
          </InputLabel>
        )}
        <Input
          id={id}
          type={defaultType}
          value={this.state.value}
          onChange={this._handleChange}
          onBlur={onBlur}
          onFocus={onFocus}
          readOnly={readOnly}
          required={required}
          disabled={disabled}
          multiline={multiline}
          rows={rows}
          onKeyDown={e => e.keyCode === 27 && e.target.blur()}
          inputRef={ref => (this.inputRef = ref)}
          inputComponent={inputMask ? InputMaskCustom : undefined}
          inputProps={{
            ...inputProps,
            mask: inputMask,
            theme: theme,
          }}
          endAdornment={
            <InputAdornment position="end">
              {endAdornment}
              {isPassword ? (
                <>
                  {invalid && <Icon color="error">cancel_circle</Icon>}
                  <IconButton
                    className={classes.visibilityButton}
                    aria-label="Toggle password visibility"
                    onClick={this._handleClickShowPassword}
                    onMouseDown={this._handleMouseDownPassword}
                  >
                    <Icon>{this.state.showPassword ? 'visibility' : 'visibility_off'}</Icon>
                  </IconButton>
                </>
              ) : (
                <>
                  {pending ? (
                    <CircularProgress size={20} />
                  ) : (
                    value &&
                    !errorText &&
                    invalid === false &&
                    hasExternalValidation && <Icon color="primary">check_circle</Icon>
                  )}
                </>
              )}
            </InputAdornment>
          }
        />
        <FormHelperText>{errorText && invalid ? errorText : ''}</FormHelperText>
        {tipRender && tipRender({ className: classes.tipBox })}
      </FormControl>
    )

    return form
  }

  blur = () => this.inputRef && this.inputRef.blur()

  getInputMask = () => {
    let mask
    const { data, appFrame } = this.props
    const { type, mask: maskProp, size } = data

    if (maskProp) {
      mask = maskProp
    } else if (type === 'tel') {
      const {
        settings: {
          language: { phoneMask: maskSettings },
        },
      } = appFrame
      if (maskSettings) {
        mask = maskSettings
      }
    } else if (type === 'date' && size === 'xs') {
      mask = getMobileDateMask(TextInputField._getLocale(appFrame))
    }

    // Convert array mask to string format for react-input-mask
    if (Array.isArray(mask)) {
      return mask
        .map(char => {
          if (char === '#') return '9'
          if (typeof char === 'object') return '9'
          if (/^\d$/.test(char)) return '\\' + char
          return char
        })
        .join('')
    }

    return mask
  }

  _handleChange = e => {
    this.setState({ value: e.target.value })
    const { onChange } = this.props
    onChange && onChange(e)
  }

  _handleMouseDownPassword = event => {
    event.preventDefault()
  }

  _handleClickShowPassword = () => {
    this.setState(state => ({ showPassword: !state.showPassword }))
  }

  checkValidity = () => {
    const { data, appFrame } = this.props
    let { type, required } = data

    let valid = this.inputRef && this.inputRef.checkValidity()
    if (!valid) return false

    if (type === 'tel') {
      const {
        settings: {
          language: { phoneRgx },
        },
      } = appFrame
      let value = this.getValue()
      if ((required || value.length > 0) && phoneRgx) {
        let rgx = new RegExp(phoneRgx)
        return rgx.test(value)
      }
    }

    if (type === 'date') {
      return isDateWithinConstraints(this.getValue(), data)
    }

    return true
  }

  getValue = () => {
    const {
      data: { type, size },
      appFrame,
    } = this.props
    const { value = '' } = this.state
    const locale = TextInputField._getLocale(appFrame)

    switch (type) {
      case 'tel':
        return value.replace(/[^+\d]/g, '')
      case 'date':
        if (size === 'xs') return convertToStandardFormat(value, locale)
        return value
      default:
        return value
    }
  }
}

const styles = ({ spacing: { unit } }) => ({
  passwordBullets: {
    letterSpacing: 3.8,
  },
  inputTextMulti: {
    marginTop: unit * 2,
  },
  inputText: {},
  visibilityButton: {
    right: -unit / 2,
  },
  tipBox: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: -20,
    zIndex: 100,
  },
})

const InputMaskCustom = props => {
  const { inputRef, mask, theme, placeholder, ...other } = props

  if (!mask) {
    return <input ref={inputRef} {...other} />
  }

  return (
    <InputMask
      {...other}
      mask={mask}
      maskChar={null}
      alwaysShowMask={false}
      placeholder={placeholder}
      inputRef={inputRef}
      style={theme?.direction === 'rtl' ? { direction: 'ltr', textAlign: 'left' } : undefined}
    />
  )
}

export default withAppFrame(withStyles(styles, { withTheme: true })(TextInputField))
